import { daaluuKind, type DaaluuKind, type DaaluuTile } from './daaluuKinds.js';

export function power(d: { janlii: string | null }, tile: DaaluuTile) { return tile.kindId === d.janlii ? 100 : daaluuKind(tile).rank; }
/** The strongest called-color pair is mandatory; otherwise follow color freely. */
export function legalDaaluuPairContributions(hand: DaaluuTile[], color: DaaluuKind['color']): DaaluuTile[][] {
  const candidates: DaaluuTile[][] = [];
  for (let i = 0; i < hand.length; i++) for (let j = i + 1; j < hand.length; j++) candidates.push([hand[i], hand[j]]);
  const pairs = candidates.filter(tiles => tiles[0].kindId === tiles[1].kindId && daaluuKind(tiles[0]).color === color);
  if (pairs.length) {
    const highest = Math.max(...pairs.map(tiles => daaluuKind(tiles[0]).rank));
    return pairs.filter(tiles => daaluuKind(tiles[0]).rank === highest);
  }
  const required = Math.min(2, hand.filter(tile => daaluuKind(tile).color === color).length);
  return candidates.filter(tiles => tiles.filter(tile => daaluuKind(tile).color === color).length === required);
}

export function daaluuTributeTiles(hand: DaaluuTile[], janlii: string | null): DaaluuTile[] {
  return hand.filter(tile => tile.kindId === janlii || daaluuKind(tile).rank >= 10);
}
export function daaluuRequiredTributeTiles(hand: DaaluuTile[], janlii: string | null, relay: boolean): DaaluuTile[] {
  return hand.filter(tile => tile.kindId === janlii || daaluuKind(tile).rank >= (relay ? 8 : 10));
}
export function daaluuBatchRelayTiles(hand: DaaluuTile[], janlii: string | null): DaaluuTile[] {
  return hand.filter(tile => tile.kindId !== janlii && daaluuKind(tile).rank >= 8 && daaluuKind(tile).rank < 10);
}

/** A complete ten-tile hand with no ten-or-higher tile and no called Janlii. */
export function isDaaluuAravguiHand(hand: DaaluuTile[], janlii: string | null): boolean {
  return hand.length === 10 && hand.every(tile => tile.kindId !== janlii && daaluuKind(tile).rank < 10);
}

export function legalDaaluuAravguiContributions(hand: DaaluuTile[], janlii: string | null): DaaluuTile[][] {
  const small = hand.filter(tile => tile.kindId !== janlii && daaluuKind(tile).rank < 10);
  const pool = small.length >= 2 ? small : hand;
  const pairs: DaaluuTile[][] = [];
  for (let i = 0; i < pool.length; i++) for (let j = i + 1; j < pool.length; j++) pairs.push([pool[i], pool[j]]);
  return pairs;
}

/** Any small tiles may return. Shortages require the lowest unavoidable big tiles. */
export function validDaaluuTributeReturn(hand: DaaluuTile[], janlii: string | null, count: number, ids: string[], maxRank = 10): boolean {
  if (ids.length !== count || new Set(ids).size !== count) return false;
  const selected = hand.filter(tile => ids.includes(tile.id));
  if (selected.length !== count) return false;
  const small = hand.filter(tile => power({ janlii }, tile) < maxRank);
  if (small.length >= count) return selected.every(tile => power({ janlii }, tile) < maxRank);
  if (!small.every(tile => ids.includes(tile.id))) return false;
  const big = hand.filter(tile => power({ janlii }, tile) >= maxRank).sort((a, b) => power({ janlii }, a) - power({ janlii }, b));
  const cutoff = big[count - small.length - 1];
  return !!cutoff && selected.every(tile => power({ janlii }, tile) <= power({ janlii }, cutoff)) &&
    big.filter(tile => power({ janlii }, tile) < power({ janlii }, cutoff)).every(tile => ids.includes(tile.id));
}
